import React, { useEffect, useState } from 'react'
import { Select } from '../../../atoms'
import { formatContents, Row, Rate, RatesCard } from '../rates-table'
import { RichText } from '../../helpers/react'
import { getDateString } from '..'

export type RateComparsionWrapperProps = {
  title?: string
  contents: Row[][]
  footnotes: any
  displayDate: boolean
  localDates: Date
  globalDates: Date
  dateString: string | null
}

export const RateComparsionWrapper = ({ title, contents, displayDate, localDates, globalDates, footnotes }: RateComparsionWrapperProps) => {
  const rates: Rate[] = formatContents(contents)
  const dateString = displayDate ? getDateString(localDates, globalDates) : null
  return (
    <div className='RateTableWrapper'>
      {title && <div className='RateTable__title'>{title}</div>}
      <RateComparsion rates={rates} />
      {dateString && <div className='RatesTable__date'>Date {dateString}</div>}
      {footnotes && (
        <div className='RatesTable__footnotes'>
          <RichText content={footnotes} />
        </div>
      )}
    </div>
  )
}

export const RateComparsion = ({ rates }: { rates: Rate[] }) => {
  const [lenderNames, setLenderNames] = useState<Array<string>>([...new Set(rates.map(({ lenderName }) => lenderName))])
  const [lenderNameA, setLenderNameA] = useState<string>(rates[0].lenderName)
  const [lenderNameB, setLenderNameB] = useState<string>(rates[1].lenderName)
  const [lenderRateA, setLenderRateA] = useState<Rate>(rates[0])
  const [lenderRateB, setLenderRateB] = useState<Rate>(rates[1])

  // Handlers

  const handleLenderNameAChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const lenderName = event.target.value
    setLenderNameA(event.target.value)
    setLenderRateA(rates.find((rate) => rate.lenderName === lenderName) as Rate)
  }

  const handleLenderRateAChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const rate = JSON.parse(event.target.value) as Rate
    setLenderRateA(rate)
  }

  const handleLenderNameBChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const lenderName = event.target.value
    setLenderNameB(event.target.value)
    setLenderRateB(rates.find((rate) => rate.lenderName === lenderName) as Rate)
  }

  const handleLenderRateBChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const rate = JSON.parse(event.target.value) as Rate
    setLenderRateB(rate)
  }

  return (
    <div className='RateComparsion'>
      <div className='RateComparsion__selectorRow'>
        <div className='RateComparsion__selectorWrapper'>
          <h3 className='RateComparsion__selectorTitle'>Mortgage Deal 1</h3>
          <p className='RateComparsion__selectorDesc'>
            Choose the first deal that you'd like to compare
          </p>
          <div className='RateComparsion__selectorInputs'>
            <Select
              name={'compare-lender-name-a'}
              onChange={handleLenderNameAChange}
              value={lenderNameA}
              options={lenderNames.map((lenderName) => ({
                text: lenderName,
                value: lenderName,
              }))}
            />
            <Select
              name={'compare-lender-rate-a'}
              onChange={handleLenderRateAChange}
              value={JSON.stringify(lenderRateA)}
              options={rates
                .filter(({ lenderName }) => lenderName === lenderNameA)
                .map((rate) => ({
                  text: `${rate.ltv}% LTV | ${rate.ProductName}`,
                  value: JSON.stringify(rate),
                }))
                .sort((a, b) => a.text > b.text ? 1 : -1)
              }
            />
          </div>
          <div className='RateComparsion__cardRow'>
            <RatesCard
              className='RatesCard__wrapper--compact'
              product={lenderRateA}
              clickout={clickouts[0]}
              selectedTab={0}
            />
          </div>
        </div>
        <div className='RateComparsion__selectorWrapper'>
          <h3 className='RateComparsion__selectorTitle'>Mortgage Deal 2</h3>
          <p className='RateComparsion__selectorDesc'>Compare your deal against another deal</p>
          <div className='RateComparsion__selectorInputs'>
            <Select
              name={'compare-lender-name-b'}
              onChange={handleLenderNameBChange}
              value={lenderNameB}
              options={lenderNames.map((lenderName) => ({
                text: lenderName,
                value: lenderName,
              }))}
            />
            <Select
              name={'compare-lender-rate-b'}
              onChange={handleLenderRateBChange}
              value={JSON.stringify(lenderRateB)}
              options={rates
                .filter(({ lenderName }) => lenderName === lenderNameB)
                .map((rate) => ({
                  text: `${rate.ltv}% LTV | ${rate.ProductName}`,
                  value: JSON.stringify(rate),
                }))
                .sort((a, b) => a.text > b.text ? 1 : -1)
              }
            />
          </div>
          <div className='RateComparsion__cardRow'>
            <RatesCard
              className='RatesCard__wrapper--compact'
              product={lenderRateB}
              clickout={clickouts[1]}
              selectedTab={0}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default RateComparsion

const clickouts = [
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_comparison_cta1&sig=9c2e595390dffb2833b3a7f311ee7db72626f2a1be5d11c79abdbce666da4a5f&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_comparison_cta2&sig=e4eaa084de3eee6a0f6d6581d5ff0786f13326a727a45a446402fff132a4d8ef&brand=uswitch',
]
